import React from 'react';
import { profileData, highlights } from '../data/mock';
import { Linkedin, Github } from 'lucide-react';
import { Marked } from './primitives';

const XIcon = ({ size = 24, className = '' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Hero = () => {
  // Split the two-sentence tagline so each idea gets its own line
  const taglineParts = profileData.tagline.split(/(?<=\.)\s+(?=I'm )/);
  const goTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="bg-cream relative overflow-hidden pt-24 pb-10 sm:pt-36 sm:pb-12"
    >
      {/* Faint dot grid, as on the old site */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #14140F 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="w-full max-w-5xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Status badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-tag-mint border border-tag-mint-line rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-flag" />
            <span className="text-xs font-bold text-ink">{profileData.availability}</span>
          </div>

          {/* Name */}
          <h1 className="text-[2.5rem] leading-[1.05] sm:text-6xl md:text-7xl font-bold text-ink tracking-[-0.03em]">
            Hi, I'm{' '}
            <span className="relative inline-block">
              {profileData.name}
              <svg
                className="absolute -bottom-1 sm:-bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M2 8C50 2 150 2 198 8" stroke="#E0392B" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>

          {/* Tagline: hook sentence, then what he's building on its own line */}
          <p className="mt-6 sm:mt-8 text-[1.0625rem] sm:text-[1.375rem] font-semibold text-ink max-w-[21rem] sm:max-w-2xl leading-snug text-balance">
            {taglineParts.map((part, i) => (
              <span key={i} className={i > 0 ? 'block mt-1.5 sm:mt-2' : 'block'}>
                {part}
              </span>
            ))}
          </p>
          <p className="mt-5 text-sm sm:text-base text-ink-soft max-w-xl leading-relaxed text-balance">
            <Marked text={profileData.resignation} />
          </p>

          {/* Credential pills */}
          <div className="mt-6 flex flex-wrap justify-center gap-2 sm:gap-2.5">
            {highlights.map((item) => (
              <span
                key={item}
                className="px-3 py-1.5 bg-paper border border-rule rounded-full text-xs sm:text-[13px] font-medium text-ink"
              >
                {item}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row w-full max-w-[17rem] sm:w-auto sm:max-w-none justify-center gap-3 sm:gap-4">
            <button
              onClick={() => goTo('#next')}
              className="px-5 py-2.5 bg-ink text-cream text-sm font-semibold rounded-lg border-[1.5px] border-ink shadow-hard-sm hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
            >
              What I'm building
            </button>
            <button
              onClick={() => goTo('#emergent')}
              className="px-5 py-2.5 bg-paper text-ink text-sm font-semibold rounded-lg border-[1.5px] border-ink shadow-hard-sm hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
            >
              What I built at Emergent
            </button>
          </div>

          {/* Socials */}
          <div className="mt-7 flex items-center justify-center">
            <div className="flex items-center gap-5 text-subtle">
              <a
                href={profileData.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ink transition-colors p-2 -m-2"
                title="LinkedIn"
                aria-label="LinkedIn"
              >
                <Linkedin size={19} />
              </a>
              <a
                href={profileData.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ink transition-colors p-2 -m-2"
                title="X (Twitter)"
                aria-label="X (Twitter)"
              >
                <XIcon size={17} />
              </a>
              <a
                href={profileData.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ink transition-colors p-2 -m-2"
                title="GitHub"
                aria-label="GitHub"
              >
                <Github size={19} />
              </a>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
